import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { BeneficiaryDetailsCurrency, USDBeneficiaryOptions } from 'types/beneficiaries'
import { ContactFiatInstrumentFormData } from '../schemas/contactFiatInstrumentSchema'

type UseFormFieldOptionsProps = {
  formValues: ContactFiatInstrumentFormData
}

export const useFormFieldOptions = ({ formValues }: UseFormFieldOptionsProps) => {
  const { t } = useTranslation()

  const { accountCapabilities } = useAccountsContext()
  const {
    isCubixBeneficiaryCapabilityEnabled,
    isUsdAchFedwireBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
  } = accountCapabilities ?? {}

  const getAccountTypeOption = (leftText: string, value: string) => ({
    leftText,
    value,
  })

  const accountTypeOptions = useMemo(() => {
    switch (formValues?.currency) {
      case BeneficiaryDetailsCurrency.USD:
        return [
          getAccountTypeOption(t('Checking'), 'CHECKING'),
          getAccountTypeOption(t('Savings'), 'SAVINGS'),
        ]
      case BeneficiaryDetailsCurrency.ZAR:
        return [
          getAccountTypeOption(t('cheque'), 'CHEQUE'),
          getAccountTypeOption(t('savings'), 'SAVINGS'),
          getAccountTypeOption(t('transmission'), 'TRANSMISSION'),
        ]
      default:
        return
    }
  }, [formValues?.currency])

  const getPaymentMethodOption = (label: string, value: string) => ({
    leftText: label,
    value,
  })

  const paymentMethodOptions = useMemo(() => {
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return []

    const options = []
    if (isCubixBeneficiaryCapabilityEnabled) {
      options.push(
        getPaymentMethodOption(
          t('manageBeneficiaries.cubixPaymenthMethodOption'),
          USDBeneficiaryOptions.CUBIX
        )
      )
    }
    if (isUsdAchFedwireBeneficiaryCapabilityEnabled) {
      options.push(
        getPaymentMethodOption(t('manageBeneficiaries.ach'), USDBeneficiaryOptions.ACH),
        getPaymentMethodOption(t('manageBeneficiaries.fedwire'), USDBeneficiaryOptions.FEDWIRE)
      )
    }
    if (isUsdCRNowBeneficiaryCapabilityEnabled) {
      options.push(
        getPaymentMethodOption(t('manageBeneficiaries.crNow'), USDBeneficiaryOptions.CRNOW)
      )
    }

    if (isUsdRtpBeneficiaryCapabilityEnabled) {
      options.push(getPaymentMethodOption(t('manageBeneficiaries.rtp'), USDBeneficiaryOptions.RTP))
    }

    if (isUsdFedNowBeneficiaryCapabilityEnabled) {
      options.push(
        getPaymentMethodOption(t('manageBeneficiaries.fedNow'), USDBeneficiaryOptions.FEDNOW)
      )
    }

    return options
  }, [
    isCubixBeneficiaryCapabilityEnabled,
    isUsdAchFedwireBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    formValues?.currency,
  ])

  return useMemo(() => {
    return {
      accountTypeOptions,
      paymentMethodOptions,
    }
  }, [accountTypeOptions, paymentMethodOptions])
}
